import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Cancel as ErrorIcon
} from '@mui/icons-material';
import { ScraperService } from '../../services/scraper.service';
import { ScraperInfoUi } from '../../schemas/scraper/scraper-info';

interface ScraperInfoModalProps {
  open: boolean;
  onClose: () => void;
}

const ScraperInfoModal: React.FC<ScraperInfoModalProps> = ({ open, onClose }) => {
  const [scraperInfo, setScraperInfo] = useState<ScraperInfoUi | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchScraperInfo();
    }
  }, [open]);

  const fetchScraperInfo = async () => {
    setLoading(true);
    try {
      const info = await ScraperService.obtenerInfoScraper();
      setScraperInfo(info);
    } catch (error) {
      console.error('Error al obtener información del scraper:', error);
    } finally {
      setLoading(false);
    }
  };

  const allOperationsSucceeded = scraperInfo?.operaciones?.length
    ? scraperInfo.operaciones.every(op => op.exito)
    : false;

  const hasOperations = (scraperInfo?.operaciones?.length ?? 0) > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Última Ejecución del Scraper
          </Typography>
        </Box>
        <Button
          onClick={onClose}
          sx={{ minWidth: 'auto', p: 0.5 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : scraperInfo?.hasError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              Error al obtener información
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
              {scraperInfo.error || 'No se pudo obtener la información del scraper'}
            </Typography>
          </Alert>
        ) : scraperInfo && (scraperInfo.fechaInicio || scraperInfo.fechaFin) ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {allOperationsSucceeded ? (
                <SuccessIcon color="success" />
              ) : hasOperations ? (
                <ErrorIcon color="error" />
              ) : (
                <SuccessIcon color="success" />
              )}
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {allOperationsSucceeded
                  ? 'Ejecución exitosa'
                  : hasOperations
                    ? 'Ejecución con errores'
                    : 'Ejecución completada'}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {scraperInfo.fechaInicioFormatted && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Fecha de Inicio
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {scraperInfo.fechaInicioFormatted}
                  </Typography>
                </Box>
              )}

              {scraperInfo.fechaFinFormatted && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Fecha de Fin
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {scraperInfo.fechaFinFormatted}
                  </Typography>
                </Box>
              )}

              {scraperInfo.totalTimeFormatted && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Duración Total
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {scraperInfo.totalTimeFormatted}
                  </Typography>
                </Box>
              )}
            </Box>

            {hasOperations && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>
                    Desglose de operaciones
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {scraperInfo.operaciones!.map((op, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {op.exito ? (
                          <SuccessIcon sx={{ fontSize: 18 }} color="success" />
                        ) : (
                          <ErrorIcon sx={{ fontSize: 18 }} color="error" />
                        )}
                        <Typography variant="body2">
                          {op.titulo}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </>
            )}
          </Box>
        ) : (
          <Alert severity="info">
            No hay información disponible sobre la última ejecución del scraper.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScraperInfoModal;
